// @flow
import { Record, List, Map } from "immutable"
import { isPast, addMonths, addDays, differenceInDays, endOfDay, subDays, isBefore } from "date-fns"
import { ifElse, always } from "ramda"

import { FORM_FIELDS } from "common/consts/form"
import { PLACE_TYPES } from "common/consts/place"
import {
  nextDate,
  isSingleDate,
  createAnytime,
  createTimeToStay,
  createDateRange,
  createNoReturn,
  isDateRange,
  isNoReturn,
  isTimeToStay,
  createRangeAround,
  getId,
  toDateRange,
  isAnytime,
} from "common/records/SearchDate/utils"
import { getSalesmanDefault } from "common/records/Salesman/utils"
import { DEFAULT_CABIN_CLASS } from "common/records/CabinClass"
import { DATE_TYPES } from "common/records/SearchDate/SearchDate"
import RESULTS_VIEW_TYPES from "common/consts/resultViewTypes"
import SearchPlace from "common/records/SearchPlace"
import MultiPlace from "common/records/MultiPlace"
import PassengersCount from "common/records/PassengersCount"
import type Place from "common/records/Place"
import type { SearchDate } from "common/records/SearchDate/SearchDate"
import type Options from "common/records/Options"
import type { CabinClass } from "common/records/CabinClass"
import MulticityFormData from "./MulticityFormData"
import SalesmanFormData from "./SalesmanFormData"
import type { Index, IncompleteDataType, DateField, SearchMode } from "./SearchFormState.types"

const SearchFormStateRecord = Record({
  outboundDate: createDateRange(new Date(), new Date(), true),
  inboundDate: createNoReturn(true),
  origin: new MultiPlace(),
  destination: new MultiPlace(),
  passengers: new PassengersCount(),
  cabinClass: DEFAULT_CABIN_CLASS,
  multicity: List(),
  salesman: Map({
    routes: List(),
    isReturnSame: true,
    setReturnDate: false,
  }),
  pastDateUpdated: false,
  searchModeUpdated: false,
  originTips: [],
  destinationTips: [],
})

const byOutboundDate = (prev, next) => (prev.outboundDate.from > next.outboundDate.from ? 1 : -1)

const returnSameAsOrigin = (originPlaces: MultiPlace, returnPlaces: MultiPlace): boolean =>
  originPlaces.size === returnPlaces.size &&
  originPlaces.every((value, index) => value.equals(returnPlaces.get(index)))

const isOriginIncomplete = (multicityData: MulticityFormData) =>
  multicityData.origin.isEmpty() || multicityData.origin.isAnywhere()

const isDestinationIncomplete = (
  multicityData: MulticityFormData,
  index: number,
  multicitySize: number,
) =>
  index + 1 < multicitySize &&
  (multicityData.destination.isEmpty() || multicityData.destination.isAnywhere())

// Clears invalid dates in muticity trips:
// - date range of trip B cannot start before date range of trip A, etc.
// Does not clear dates in past/too much in future
const clearInvalidMulticityDates = (_multicity: List<MulticityFormData>) =>
  _multicity.withMutations(multicity => {
    multicity.reduce((minDate, trip, index) => {
      const date = trip.outboundDate

      if (!isDateRange(date)) {
        return minDate
      }

      if (minDate && isBefore(date.from, minDate)) {
        multicity.setIn([index, "outboundDate"], createAnytime(true))

        return minDate
      }

      return date.from
    }, null)
  })

// Clears invalid dates in salesman:
// - outbound date range cannot start before inbound date range
// - date ranges of via routes must be between outbound and inbound date range
// - outbound and via routes date ranges must end at least one day before end of inbound date range
// Clears date range even if it's not enabled
// Does not take trip length into consideration
// Does not clear dates in past/too much in future
const clearInvalidSalesmanDates = (_salesman: Map) =>
  _salesman.withMutations(salesman => {
    salesman.update("routes", _routes =>
      _routes.withMutations(routes => {
        const outboundDate = routes.getIn([0, "dateRange"])
        const minDate = isDateRange(outboundDate) ? outboundDate.from : null
        const inboundDate = routes.getIn([1, "dateRange"])
        const hasMaxDateSet = salesman.get("setReturnDate") && isDateRange(inboundDate)
        const maxDate =
          hasMaxDateSet && (!minDate || isBefore(minDate, inboundDate.from)) ? inboundDate.to : null

        if (!minDate && !maxDate) {
          return
        }

        if (hasMaxDateSet && !maxDate) {
          routes.setIn([1, "dateRange"], createAnytime(true))
        }

        routes.forEach((route, index) => {
          if (index <= 1) {
            return
          }

          const routeDate = route.dateRange

          if (!isDateRange(routeDate)) {
            return
          }

          if (
            (minDate && isBefore(routeDate.from, minDate)) ||
            (maxDate && !isBefore(routeDate.to, maxDate))
          ) {
            routes.setIn([index, "dateRange"], createAnytime(true))
          }
        })
      }),
    )
  })

const addMulticityTrip = (
  _multicity: List<MulticityFormData>,
  origin: MultiPlace,
  destination: MultiPlace,
) =>
  _multicity.withMutations(multicity => {
    // if first date is not set => set it to default date range
    multicity.updateIn([0, "outboundDate"], outboundDate =>
      !outboundDate.from ? toDateRange(new Date()) : outboundDate,
    )

    const lastWithDateRange = multicity.findLast(trip => isDateRange(trip.outboundDate))

    multicity.push(
      new MulticityFormData({
        origin,
        destination,
        outboundDate: nextDate(lastWithDateRange.outboundDate),
      }),
    )
  })

class SearchFormState extends SearchFormStateRecord {
  outboundDate: SearchDate

  inboundDate: SearchDate

  origin: MultiPlace

  destination: MultiPlace

  passengers: PassengersCount

  cabinClass: CabinClass

  multicity: List<MulticityFormData>

  salesman: Map<{ routes: List<SalesmanFormData>, isReturnSame: boolean, setReturnDate: boolean }>

  correctDatePair(direction: string): SearchFormState {
    let data = this
    if (direction !== "outboundDate" && direction !== "inboundDate") {
      return data // direction not a date
    }
    if (!data.get("outboundDate").from || !data.get("inboundDate").from) {
      return data // nothing to correct
    }
    if (isAnytime(data.get("outboundDate")) || isAnytime(data.get("inboundDate"))) {
      return data
    }
    if (isNoReturn(data.get("outboundDate")) || isNoReturn(data.get("inboundDate"))) {
      return data
    }
    if (isTimeToStay(data.get("inboundDate"))) {
      return data
    }

    const crossedFrom = data.get("outboundDate").from > data.get("inboundDate").from
    const crossedTo = data.get("outboundDate").to > data.get("inboundDate").to

    if (crossedFrom || crossedTo) {
      if (direction === FORM_FIELDS.OUTBOUND_DATE) {
        data = data.set(FORM_FIELDS.INBOUND_DATE, createTimeToStay())
      } else {
        // direction is inboundDate
        const oppositeDirection = FORM_FIELDS.OUTBOUND_DATE
        const newPlain = createDateRange(
          data.get(oppositeDirection).from,
          data.get(oppositeDirection).to,
        )
        if (crossedFrom) {
          newPlain.from = data.get(direction).from
        }
        if (crossedTo) {
          newPlain.to = data.get(direction).to
        }
        data = data.set(oppositeDirection, newPlain)
      }
    }

    return data
  }

  adjustPathForSalesman() {
    return this.getMode() === "salesman" ? ["salesman", "routes"] : ["multicity"]
  }

  // I'll just leave a few methods here to help ease us into a major refactoring…
  getPlace(field: "origin" | "destination", index?: Index): MultiPlace {
    const $index = this.parseIndex(index)
    return this.getIn(
      $index === undefined ? [field] : [...this.adjustPathForSalesman(), $index, field],
    )
  }

  getDate(
    field: "outboundDate" | "inboundDate" | "dateRange" | "timeOfStay",
    index?: Index,
  ): SearchDate {
    const $index = this.parseIndex(index)
    return this.getIn(
      $index === undefined ? [field] : [...this.adjustPathForSalesman(), $index, field],
    )
  }

  setPlace(
    multiPlace: MultiPlace | (MultiPlace => MultiPlace),
    field: "origin" | "destination",
    index?: Index,
  ): SearchFormState {
    const $index = this.parseIndex(index)
    const setPath =
      $index === undefined ? [field] : [...this.adjustPathForSalesman(), $index, field]
    return typeof multiPlace === "function"
      ? this.updateIn(setPath, multiPlace)
      : this.setIn(setPath, multiPlace)
  }

  setDate(
    date: SearchDate | (SearchDate => SearchDate),
    field: DateField,
    index?: Index,
    isWowAir?: boolean, // F o r   s o m e   r e a s o n
  ): SearchFormState {
    const $index = this.parseIndex(index)
    const setPath =
      $index === undefined ? [field] : [...this.adjustPathForSalesman(), $index, field]
    let newForm =
      typeof date === "function" ? this.updateIn(setPath, date) : this.setIn(setPath, date)
    if (newForm.getMode() === "multicity" && field === "outboundDate") {
      let counter = $index + 1
      while (
        newForm.multicity.get(counter) &&
        newForm.multicity.get(counter).outboundDate.isDefault
      ) {
        newForm = newForm.setIn(
          ["multicity", counter, "outboundDate"],
          nextDate(newForm.multicity.get(counter - 1).outboundDate),
        )
        counter += 1
      }
    }
    return isWowAir ? newForm : newForm.correctDatePair(field)
  }

  parseIndex(index?: Index): ?number {
    if (this.getMode() === "multicity" || this.getMode() === "salesman") {
      if (index === "first") {
        return 0
      }
      if (index === "last") {
        if (this.getMode() === "salesman") {
          return 1
        }
        return this.getMode() === "salesman"
          ? this.getIn([this.getMode(), "routes"]).count() - 1
          : this.get(this.getMode()).count() - 1
      }
      return index
    }
    return undefined
  }
  // That's all ^^

  getSearchId(): string {
    return `${this.get("origin").getId()}|${this.get("destination").getId()}|${getId(
      this.get("outboundDate"),
    )}|${getId(this.get("inboundDate"))}`
  }

  getMode(): SearchMode {
    if (this.get("multicity").count()) {
      return "multicity"
    }
    if (this.getIn(["salesman", "routes"]).count()) {
      return "salesman"
    }
    if (isNoReturn(this.get("inboundDate"))) {
      return "oneWay"
    }
    return "return"
  }

  setMode(mode: SearchMode, places: Map<string, Place>) {
    if (mode === "oneWay") {
      return this.setNoReturn()
    }
    if (mode === "return") {
      return this.setReturn()
    }
    if (mode === "salesman") {
      return this.setSalesman(places)
    }
    return this.setMulticity()
  }

  getPlaces() {
    switch (this.getMode()) {
      case "multicity":
        return this.multicity
          .reduce(
            (acc, item) =>
              acc.concat(item.origin.places.toArray()).concat(item.destination.places.toArray()),
            [],
          )
          .filter(Boolean)
      case "salesman":
        return this.salesman
          .get("routes")
          .reduce((acc, item) => acc.concat(item.destination.places.toArray()), [])
          .filter(Boolean)
      default:
        return this.origin.places
          .toArray()
          .concat(this.destination.places.toArray())
          .filter(Boolean)
    }
  }

  clearInvalidDates() {
    const mode = this.getMode()

    if (mode === "multicity") {
      return this.update("multicity", clearInvalidMulticityDates)
    }

    if (mode === "salesman") {
      return this.update("salesman", clearInvalidSalesmanDates)
    }

    return this
  }

  getViaPlaces() {
    return this.getMode() === "salesman"
      ? this.salesman
          .get("routes")
          .skip(2)
          .reduce((acc, item) => acc.concat(item.destination.places.toArray()), [])
          .filter(Boolean)
      : []
  }

  addingAnywhereToMulticity() {
    const lastMulticity = this.get("multicity").last()

    return (
      this.getMode() === "multicity" &&
      (lastMulticity.destination.isAnywhere() || lastMulticity.destination.getId() === "-")
    ) // Unselected
  }

  setReturn() {
    if (this.getMode() === "return") return this
    let newData = this // eslint-disable-line consistent-this

    if (this.getMode() === "multicity") {
      // Set origin and destination from first item of multicity
      const firstTrip = newData.get("multicity").get(0)
      newData = newData.set("origin", firstTrip.get("origin"))
      newData = newData.set("destination", firstTrip.get("destination"))
      newData = newData.set("outboundDate", firstTrip.get("outboundDate"))
      if (newData.get("multicity").get(1)) {
        newData = newData.set(
          "inboundDate",
          newData
            .get("multicity")
            .get(1)
            .get("outboundDate"),
        )
      } else {
        newData = newData.set("inboundDate", createTimeToStay(2, 10))
      }
    } else if (this.getMode() === "salesman") {
      const destinationIndex = newData.getIn(["salesman", "isReturnSame"])
        ? newData.getIn(["salesman", "routes"]).findLastIndex(route => !route.destination.isEmpty())
        : 1

      // Set origin and destination from first item of multicity
      newData = newData.set("origin", newData.getFirstOrigin())
      newData = newData.set(
        "destination",
        newData.getIn(["salesman", "routes", destinationIndex, "destination"]),
      )
      newData = newData.set(
        "outboundDate",
        newData
          .getIn(["salesman", "routes"])
          .get(0)
          .get("dateRange"),
      )
      newData = newData.set(
        "inboundDate",
        newData
          .getIn(["salesman", "routes"])
          .get(destinationIndex)
          .get("dateRange"),
      )
    } else {
      newData = newData.set("inboundDate", createAnytime(true))
    }

    newData = newData.set("multicity", List())
    newData = newData.set(
      "salesman",
      new Map({ isReturnSame: true, setReturnDate: false, routes: new List() }),
    )

    return newData
  }

  setDefaultDateIfPast() {
    if (
      this.getMode() === "multicity" &&
      !isPast(
        endOfDay(
          this.get("multicity")
            .get(0)
            .get("outboundDate").to,
        ),
      )
    ) {
      return this
    }
    if (this.getMode() !== "multicity" && !isPast(endOfDay(this.get("outboundDate").to))) {
      return this
    }

    let newData = this

    const defaultOutboundDate = createDateRange(new Date(), addMonths(new Date(), 1))

    if (this.getMode() === "oneWay") {
      newData = newData.set("outboundDate", defaultOutboundDate)
    } else if (this.getMode() === "return") {
      const defaultInboundDate = createTimeToStay(2, 10)
      newData = newData.set("outboundDate", defaultOutboundDate)
      newData = newData.set("inboundDate", defaultInboundDate)
    } else {
      const multicityList = List(
        newData.get("multicity").map((item, index) => {
          let newOutboundDate
          if (index === 0) {
            newOutboundDate = defaultOutboundDate
          } else {
            newOutboundDate = createDateRange(
              addDays(addMonths(new Date(), 1), (index - 1) * 6 + 3),
              addDays(addMonths(new Date(), 1), (index - 1) * 6 + 6),
            )
          }

          return new MulticityFormData({
            origin: item.origin,
            destination: item.destination,
            outboundDate: newOutboundDate,
          })
        }),
      )

      newData = newData.set("multicity", multicityList)
    }
    newData = newData.set("pastDateUpdated", true)

    return newData
  }

  setDefaultMode(firstEnabled: string, places: Map<string, Place>) {
    switch (firstEnabled) {
      case "oneway":
        return this.setNoReturn()
      case "return":
        return this.setDefaultDateIfPast()
      case "multicity":
        return this.setMulticity()
      case "salesman":
        return this.setSalesman(places)
      default:
        return this
    }
  }

  setDefaultModeIfDisabled(
    modesEnabled: Map<string, Map<"enabled", boolean>>,
    places: Map<string, Place>,
  ) {
    if (!modesEnabled) return this
    const [firstEnabled] = modesEnabled.findEntry((key, value) =>
      modesEnabled.getIn([value, "enabled"]),
    )
    if (!firstEnabled) {
      console.error("Error: no search mode enabled in brand config, what should I do?")
      return this
    }
    if (
      (this.getMode() === "oneWay" && !modesEnabled.getIn(["oneway", "enabled"])) ||
      (this.getMode() === "return" && !modesEnabled.getIn(["return", "enabled"])) ||
      (this.getMode() === "multicity" && !modesEnabled.getIn(["multicity", "enabled"])) ||
      (this.getMode() === "salesman" && !modesEnabled.getIn(["salesman", "enabled"]))
    ) {
      return this.setDefaultMode(firstEnabled, places).set("searchModeUpdated", !this.isDefault())
    }
    return this
  }

  setNoReturn() {
    if (this.getMode() === "oneWay") return this
    let newData = this // eslint-disable-line consistent-this

    if (this.getMode() === "multicity") {
      // Set origin and destination from first item of multicity
      const firstTrip = newData.get("multicity").get(0)
      newData = newData.set("origin", firstTrip.get("origin"))
      newData = newData.set("destination", firstTrip.get("destination"))
      newData = newData.set("outboundDate", firstTrip.get("outboundDate"))
    } else if (this.getMode() === "salesman") {
      const destinationIndex = newData.getIn(["salesman", "isReturnSame"])
        ? newData.getIn(["salesman", "routes"]).findLastIndex(route => !route.destination.isEmpty())
        : 1

      newData = newData.set("outboundDate", newData.getIn(["salesman", "routes", 0, "dateRange"]))
      newData = newData.set("origin", newData.getIn(["salesman", "routes", 0, "destination"]))
      newData = newData.set(
        "destination",
        newData.getIn(["salesman", "routes", destinationIndex, "destination"]),
      )
    }

    newData = newData.set("multicity", List())
    newData = newData.set(
      "salesman",
      new Map({
        routes: new List(),
        isReturnSame: true,
        setReturnDate: false,
      }),
    )
    newData = newData.set("inboundDate", createNoReturn())
    return newData
  }

  setMulticity() {
    if (this.getMode() === "multicity") return this

    const newOutboundDate = isDateRange(this.outboundDate)
      ? this.outboundDate
      : nextDate(new Date())

    if (
      this.destination.getId() === "anywhere" ||
      isNoReturn(this.inboundDate) ||
      this.origin.isEmpty() ||
      this.destination.isEmpty()
    ) {
      return this.set(
        "multicity",
        List([
          new MulticityFormData({
            origin: this.origin,
            destination: this.destination,
            outboundDate: newOutboundDate,
          }),
        ]),
      ).set("cabinClass", DEFAULT_CABIN_CLASS)
    }

    return this.set(
      "multicity",
      List([
        new MulticityFormData({
          origin: this.origin,
          destination: this.destination,
          outboundDate: newOutboundDate,
        }),
        new MulticityFormData({
          origin: this.destination,
          destination: this.origin.setNotDefault(),
          outboundDate: isDateRange(this.inboundDate)
            ? this.inboundDate
            : nextDate(newOutboundDate),
        }),
      ]),
    ).set("cabinClass", DEFAULT_CABIN_CLASS)
  }

  setSalesman(places: Map<string, Place>, viaPlaces: ?Array<SearchPlace> = null) {
    const mode = this.getMode()
    if (mode === "salesman") return this
    let newForm = this.set("cabinClass", DEFAULT_CABIN_CLASS)
    if (mode === "multicity") {
      // Set origin and destination from first item of multicity
      const routes = new List([
        new SalesmanFormData({
          destination: getSalesmanDefault(
            this.get("multicity")
              .get(0)
              .get("origin"),
            places,
          ),
          dateRange: this.get("multicity")
            .get(0)
            .get("outboundDate"),
        }),
        new SalesmanFormData({
          destination: getSalesmanDefault(
            this.get("multicity")
              .last()
              .get("destination"),
            places,
          ),
          dateRange: createAnytime(true),
        }),
      ])

      newForm = newForm.set(
        "salesman",
        new Map({
          isReturnSame: returnSameAsOrigin(
            routes.getIn([0, "destination", "places"]),
            routes.getIn([1, "destination", "places"]),
          ),
          setReturnDate: false,
          routes,
        }),
      )
      newForm = newForm.set("multicity", List())
    } else {
      const origin = getSalesmanDefault(this.origin, places)
      newForm = newForm.set(
        "salesman",
        new Map({
          isReturnSame: mode !== "oneWay",
          setReturnDate: false,
          routes: new List([
            new SalesmanFormData({
              destination: origin,
              dateRange: isAnytime(this.outboundDate)
                ? createDateRange(new Date(), addMonths(new Date(), 1))
                : this.outboundDate,
            }),
            new SalesmanFormData(
              mode === "oneWay"
                ? {
                    destination: getSalesmanDefault(this.destination, places),
                    dateRange: this.inboundDate,
                  }
                : {
                    destination: origin,
                    dateRange: this.inboundDate,
                  },
            ),
          ]),
        }),
      )

      if (!viaPlaces) {
        let destinationVia = getSalesmanDefault(this.destination, places, true)
        if (destinationVia.places.size > 0 && origin.contains(destinationVia.places.get(0))) {
          destinationVia = new MultiPlace({
            places: List(),
          })
        }
        const outboundDate = newForm.getIn(["salesman", "routes", 0, "dateRange"])
        newForm = newForm.updateIn(["salesman", "routes"], routes =>
          routes.concat([
            new SalesmanFormData({
              destination: destinationVia,
              dateRangeEnabled: false,
              dateRange: createDateRange(outboundDate.from, addDays(outboundDate.from, 5)),
            }),
          ]),
        )
      }
    }

    if (viaPlaces) {
      const viaRoutes = viaPlaces.map(
        viaPlace =>
          new SalesmanFormData({
            destination: getSalesmanDefault(
              new MultiPlace({
                places: List([viaPlace]),
              }),
              places,
              true,
            ),
            dateRangeEnabled: false,
          }),
      )

      newForm = newForm.updateIn(["salesman", "routes"], routes => routes.concat(viaRoutes))
    }

    return newForm.getIn(["salesman", "routes"]).size >= 4
      ? newForm
      : newForm.addEmptySalesmanTrip()
  }

  addMulticityTrip(destination?: MultiPlace = MultiPlace.anywhere()) {
    const lastTrip = this.get("multicity").last()

    return this.update("multicity", multicity =>
      addMulticityTrip(multicity, lastTrip.destination, destination),
    )
  }

  addCustomMulticityStop(multiplace: MultiPlace, direction: string) {
    const lastTrip = this.get("multicity").last()

    return this.update("multicity", multicity =>
      addMulticityTrip(
        multicity,
        direction === "origin" ? multiplace : lastTrip.destination,
        direction === "destination"
          ? multiplace
          : new MultiPlace({
              places: List([new SearchPlace({ mode: "anywhere" })]),
            }),
      ),
    )
  }

  addEmptySalesmanTrip() {
    const outboundDate = this.getIn(["salesman", "routes", 0, "dateRange"])
    const inboundDate = this.getIn(["salesman", "setReturnDate"])
      ? this.getIn(["salesman", "routes", 1, "dateRange"])
      : null

    const prevRouteWithDateRange = this.getIn(["salesman", "routes"])
      .delete(1) // skip inbound
      .findLast(route => isDateRange(route.dateRange))
    const prevDate = prevRouteWithDateRange ? prevRouteWithDateRange.dateRange : outboundDate

    const nextDateRange = nextDate(prevDate)
    const dateRange = createDateRange(
      inboundDate && isDateRange(inboundDate) && !isBefore(nextDateRange.from, inboundDate.to)
        ? prevDate.to
        : nextDateRange.from,
      inboundDate && isDateRange(inboundDate) && !isBefore(nextDateRange.to, inboundDate.to)
        ? subDays(inboundDate.to, 1)
        : nextDateRange.to,
    )

    const newTrip = new SalesmanFormData({
      destination: new MultiPlace({ places: List() }),
      timeOfStay: createTimeToStay(3, 5, true),
      dateRange,
      dateRangeEnabled: false,
    })
    return this.updateIn(["salesman", "routes"], salesman => salesman.push(newTrip))
  }

  removeMulticityTrip(i: number) {
    const newForm = this.update("multicity", multicity => multicity.delete(i))
    return i === 0
      ? newForm.setIn(
          ["multicity", 0, "origin"],
          newForm.getIn(["multicity", 0, "origin"]).removeSpecial(),
        )
      : newForm
  }

  removeSalesmanTrip(i: number) {
    return this.updateIn(["salesman", "routes"], salesman => salesman.delete(i))
  }

  salesmanUnsupportedPlacesPresent() {
    const anyEmpty =
      this.salesman
        .get("routes")
        .filter(item => item.destination.places.count() === 0)
        .count() > 0
    const unsupportedPlace =
      this.salesman
        .get("routes")
        .filter(
          item =>
            item.destination.places
              .filter(
                place =>
                  ![
                    PLACE_TYPES.CITY,
                    PLACE_TYPES.AIRPORT,
                    PLACE_TYPES.BUS_STATION,
                    PLACE_TYPES.TRAIN_STATION,
                  ].includes(place.value.type),
              )
              .count() > 0,
        )
        .count() > 0
    return anyEmpty || unsupportedPlace
  }

  useAggregateResults(options: Options) {
    return (
      this.getMode() !== "multicity" &&
      this.getMode() !== "salesman" &&
      (this.get("destination").isLarge() || this.get("destination").isEmpty()) &&
      options.get("preferredResultsView") !== RESULTS_VIEW_TYPES.LIST
    )
  }

  areTripsOverlapping() {
    this.multicity
      .sort(byOutboundDate)
      .some(
        (trip, index, multicity) =>
          index > 0 && multicity.get(index - 1).outboundDate.to > trip.outboundDate.from,
      )
  }

  isIncomplete(allowDestEmpty: boolean = false) {
    if (this.getMode() === "multicity") {
      return this.isMulticityIncomplete()
    }
    if (this.getMode() === "salesman") {
      return this.salesman
        .get("routes")
        .some(
          (route, index) =>
            route.destination.isEmpty() ||
            (index > 1 &&
              ((route.dateRangeEnabled && !isDateRange(route.dateRange)) ||
                (route.timeOfStayEnabled && !isTimeToStay(route.timeOfStay)))),
        )
    }
    return this.origin.isEmpty() || (!allowDestEmpty && this.destination.isEmpty())
  }

  isEmpty(index: number) {
    if (this.getMode() === "multicity") {
      const multicityTrip = this.multicity.get(index)

      return multicityTrip && multicityTrip.origin.isEmpty() && multicityTrip.destination.isEmpty()
    }
    return this.origin.isEmpty() && this.destination.isEmpty()
  }

  shouldFetchPopular() {
    return this.destination.places.size === 0
  }

  changeToCheaperDateOption() {
    const searchForm = this
    const index = searchForm.getMode() === "multicity" ? 0 : undefined
    const oldOutbound = searchForm.getDate("outboundDate", index)
    const oldInbound = searchForm.get("inboundDate")

    if (oldOutbound.type !== DATE_TYPES.DATE_RANGE) {
      return searchForm
    }

    if (searchForm.getMode() === "oneWay" && isSingleDate(oldOutbound)) {
      return searchForm.set("outboundDate", createRangeAround(oldOutbound.from))
    }
    if (
      searchForm.getMode() === "return" &&
      (isSingleDate(oldOutbound) || isSingleDate(oldInbound))
    ) {
      let newOutbound
      let newInbound

      if (isSingleDate(oldOutbound)) {
        const diff = differenceInDays(oldInbound.from, oldOutbound.from)

        if (isSingleDate(oldInbound)) {
          if (diff >= 2 && diff < 30) {
            newOutbound = createRangeAround(oldOutbound.from)
            newInbound = createTimeToStay(diff - 1, diff + 1)
          } else if (diff >= 30) {
            newOutbound = createRangeAround(oldOutbound.from)
            newInbound = createRangeAround(oldInbound.from)
          }
        } else if (isDateRange(oldInbound)) {
          if (diff >= 2) {
            newOutbound = createRangeAround(oldOutbound.from)
          }
        } else if (isTimeToStay(oldInbound)) {
          if (oldInbound.max >= 2) {
            newOutbound = createRangeAround(oldOutbound.from)
          }
        }
      } else if (isSingleDate(oldInbound) && isDateRange(oldOutbound)) {
        const diff = differenceInDays(oldInbound.from, oldOutbound.to)
        if (diff >= 2 && diff < 30) {
          newInbound = createTimeToStay(diff - 1, diff + 1)
        } else if (diff >= 30) {
          newInbound = createRangeAround(oldInbound.from)
        }
      }
      return searchForm
        .set("outboundDate", newOutbound || oldOutbound)
        .set("inboundDate", newInbound || oldInbound)
    }
    if (searchForm.getMode() === "multicity" && isSingleDate(oldOutbound)) {
      return searchForm.setDate(createRangeAround(oldOutbound.from), "outboundDate", index)
    }
    return searchForm
  }

  isMulticityIncomplete(): boolean {
    return Boolean(this.getIncompleteMulticityFieldInfo())
  }

  getIncompleteMulticityFieldInfo(): ?IncompleteDataType {
    if (this.getMode() !== "multicity") return null

    const incompleteIndex = this.multicity.findKey(
      (multicityData, index) =>
        isOriginIncomplete(multicityData) ||
        isDestinationIncomplete(multicityData, index, this.multicity.size) ||
        !isDateRange(multicityData.outboundDate),
    )

    if (isNaN(incompleteIndex)) return null

    const field = ifElse(
      isOriginIncomplete,
      always("origin"),
      ifElse(
        input => isDestinationIncomplete(input, incompleteIndex, this.multicity.size),
        always("destination"),
        always("outboundDate"),
      ),
    )(this.multicity.get(incompleteIndex))

    if (field === "outboundDate" && incompleteIndex === this.multicity.size - 1) {
      return null
    }

    return {
      field,
      index: incompleteIndex,
    }
  }

  isLastOriginEmpty() {
    // Does not really make sense for salesman to ask this
    return this.getMode() === "salesman" ? false : this.getPlace("origin", "last").isEmpty()
  }

  isLastDestinationEmpty() {
    return this.getPlace("destination", "last").isEmpty()
  }

  getLastDestination() {
    return this.getPlace("destination", "last")
  }

  getFirstOrigin() {
    if (this.getMode() === "salesman") {
      // yes this is right, this is origin despite variable being called destination
      return this.salesman.getIn(["routes", 0, "destination"])
    }
    return this.getPlace("origin", "first")
  }

  isLastDestinationSet() {
    return !this.isLastDestinationEmpty()
  }

  getPlaceFields(): MultiPlace[] {
    if (this.getMode() === "multicity") {
      return this.multicity.flatMap(trip => [trip.origin, trip.destination])
    }
    if (this.getMode() === "salesman") {
      return this.salesman.get("routes").map(trip => trip.destination)
    }
    return [this.origin, this.destination]
  }

  getOrigin(index: ?number) {
    if (Number.isInteger(index)) {
      if (this.getMode() === "multicity") {
        return this.getIn(["multicity", index, "origin"])
      }
      return this.getIn(["salesman", "routes", 0, "destination"])
    }
    return this.origin
  }

  getDestinationsCount() {
    switch (this.getMode()) {
      case "multicity":
        return this.multicity.count()
      case "salesman":
        return this.getIn(["salesman", "routes"]).count() - 1
      default:
        return 1
    }
  }

  isDefault(): boolean {
    return (
      this.origin.isDefault() &&
      this.destination.isDefault() &&
      this.multicity.size === 0 &&
      this.outboundDate.isDefault &&
      this.inboundDate.isDefault
    )
  }

  // $FlowFixMe
  static fromPlainJS(input) {
    return new SearchFormState({
      outboundDate: input.outboundDate,
      inboundDate: input.inboundDate,
      origin: MultiPlace.fromPlainJS(input.origin),
      destination: MultiPlace.fromPlainJS(input.destination),
      passengers: new PassengersCount(input.passengers),
      cabinClass: input.cabinClass,
      multicity: List(input.multicity.map(MulticityFormData.fromPlainJS)),
      originTips: input.originTips.map(SearchPlace.fromPlainJS),
      destinationTips: input.destinationTips,
      salesman: new Map({
        routes: List(input.salesman.routes.map(SalesmanFormData.fromPlainJS)),
        isReturnSame: input.isReturnSame,
        setReturnDate: input.setReturnDate,
      }),
    })
  }
}

export default SearchFormState
